import React, { useRef } from "react";
import {
  OTP_LENGTH,
  applyOtpBackspace,
  applyOtpInput,
  applyOtpPaste,
  digitsFromValue,
} from "./otpSixBoxes";

interface Props {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  idPrefix?: string;
}

export function OtpSixBoxes({
  value,
  onChange,
  disabled = false,
  idPrefix = "otp",
}: Props) {
  const digits = digitsFromValue(value);
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const focusAt = (index: number) => {
    const i = Math.max(0, Math.min(OTP_LENGTH - 1, index));
    const el = refs.current[i];
    if (!el) return;
    el.focus();
    el.select();
  };

  return (
    <div className="flex justify-center gap-2" role="group" aria-label="Código de 6 dígitos">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          id={`${idPrefix}-${index}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          name={`${idPrefix}-${index}`}
          maxLength={index === 0 ? OTP_LENGTH : 1}
          value={digit}
          disabled={disabled}
          aria-label={`Dígito ${index + 1} de ${OTP_LENGTH}`}
          onChange={(e) => {
            const next = applyOtpInput(value, index, e.target.value);
            onChange(next.value);
            requestAnimationFrame(() => focusAt(next.focus));
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              e.preventDefault();
              const next = applyOtpBackspace(value, index);
              onChange(next.value);
              requestAnimationFrame(() => focusAt(next.focus));
              return;
            }
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              focusAt(index - 1);
              return;
            }
            if (e.key === "ArrowRight") {
              e.preventDefault();
              focusAt(index + 1);
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (!text) return;
            e.preventDefault();
            const next = applyOtpPaste(text);
            onChange(next.value);
            requestAnimationFrame(() => focusAt(next.focus));
          }}
          onFocus={(e) => e.target.select()}
          className="w-11 h-12 text-center text-xl font-semibold text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  );
}
